import { getEnvironmentConfig, getLogger, httpClientInterface } from 'app-components/utils/core';
import type { RecordOf } from 'immutable';
import { call, delay, put, select, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';

import { resourceSagaUtils } from 'app-components/flux/core';
import {
  type QcsSyncResourcesConfigProps,
  type QcsSyncResourcesPayloadProps,
  createTypedQcsSyncResourcesConfigFactory,
  mkQcsSyncResourcesPayload,
} from 'app-components/flux/core/resourceStoreTypes';

import { preferencesV2Actions, preferencesV2Types } from 'app-components/flux/preferences-v2';

import * as actions from './profileActions';
import { getProfile } from './profileSelectors';
import {
  AddressProps,
  Profile,
  ProfileProps,
  getVerifyStatusResponse,
  makeProfileFull,
  verifyIdentityResponse,
} from './profileTypes';

// poll every 4 seconds up to a total of 4 minutes (seconds are buffer over QCS timeouts)
const PROFILE_POLL_INTERVAL = 4;
const PROFILE_POLL_ATTEMPTS = 240 / PROFILE_POLL_INTERVAL;

const log = getLogger('flux/profile/profileSagas.ts');

type genericResponse<T> = {
  config: any;
  data: T;
  headers: any;
  request: any;
  status: number;
  statusText: string;
};

const mkQcsSyncProfileConfig = createTypedQcsSyncResourcesConfigFactory<ProfileProps>();

const mkResourceConfig = (props: Partial<QcsSyncResourcesConfigProps<ProfileProps | RecordOf<ProfileProps>>>) =>
  mkQcsSyncProfileConfig({
    resourceName: 'User Profiles',
    resourceBaseUrl: '/userprofiles',
    axiosConfig: { headers: { 'qcs-dataset-id': null } },
    getLastSyncDate: () => undefined,
    transformResourceToRequestData: (resource) => ('toJS' in resource ? resource.toJS() : resource) as ProfileProps,
    transformResponseToResource: (response) => makeProfileFull(response.data),
    ...props,
  });

const mkAddressResourceConfig = (props: Partial<QcsSyncResourcesConfigProps<AddressProps>>) =>
  createTypedQcsSyncResourcesConfigFactory<AddressProps>()({
    resourceName: 'Addresses',
    resourceBaseUrl: '/userprofiles/addresses',
    axiosConfig: { headers: { 'qcs-dataset-id': null } },
    getLastSyncDate: () => undefined,
    ...props,
  });

export function* getProfileActionWatcher() {
  const resourceConfig = mkResourceConfig({
    resourceBaseUrl: '/userprofiles/me',
    successAction: actions.getProfileSuccess,
    failureAction: actions.getProfileFailure,
    axiosConfig: {
      headers: {
        'qcs-dataset-id': null,
      },
    },
  });
  yield takeLeading(actions.getProfile.type, resourceSagaUtils.qcsSyncGetResource<ProfileProps>, resourceConfig);
}

export function* updateProfileActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.updateProfileSuccess,
    failureAction: actions.getProfileFailure,
  });
  yield takeEvery(actions.updateProfile.type, resourceSagaUtils.qcsSyncUpdateResource<ProfileProps>, resourceConfig);
}

export function* createAddressActionWatcher() {
  const resourceConfig = mkAddressResourceConfig({
    successAction: actions.createAddressSuccess,
    failureAction: actions.createAddressFailure,
    resourceBaseUrl: '/userprofiles/me/addresses',
  });
  yield takeEvery(actions.createAddress.type, resourceSagaUtils.qcsSyncCreateResource<AddressProps>, resourceConfig);
}

export function* updateAddressActionWatcher() {
  const resourceConfig = mkAddressResourceConfig({
    successAction: actions.updateAddressSuccess,
    failureAction: actions.updateAddressFailure,
  });
  yield takeEvery(actions.updateAddress.type, resourceSagaUtils.qcsSyncUpdateResource<AddressProps>, resourceConfig);
}

// this is used to poll profile service to check verification status
// can verify either primaryEmail, or primaryPhone verification
export function* pollForVerification(
  resourceToVerify: 'primaryEmail' | 'primaryPhone',
  successAction: any,
  failureAction: any,
  meta?: { resolve?: (result?: any) => void; reject?: (result?: any) => void },
): Generator {
  let response: any;
  let timeout = PROFILE_POLL_ATTEMPTS;
  while (timeout > 0) {
    try {
      log.log(`Number of times left to poll is ${timeout} #profile-verification`);
      response = yield call(
        httpClientInterface.send,
        'GET',
        `${getEnvironmentConfig().services_url}/userprofiles`,
        undefined,
        { 'qcs-dataset-id': null },
      );
      log.log('#profile-verification, poll response:', response);
      const profile = makeProfileFull(response.data.resources[0]);

      if (profile?.get(resourceToVerify)?.get('verified', false)) {
        // verified email
        yield put(
          actions.getProfileSuccess(
            mkQcsSyncResourcesPayload({ resources: [profile] }) as RecordOf<QcsSyncResourcesPayloadProps<Profile>>,
          ),
        );
        yield put(successAction());
        meta?.resolve?.(profile);
        return;
      }
    } catch (e) {
      log.warn('failure in profile verification polling\n', e);
      yield put(failureAction(e));
      meta?.reject?.(e);
      return;
    }
    timeout -= 1;
    yield delay(PROFILE_POLL_INTERVAL * 1000);
  }
  // time-out
  log.log('Time-out #profile-verification');
  meta?.reject?.('Polling timed out');
}

export function* verifyPhoneActionWatcher() {
  yield takeLatest(actions.verifyPhone.type, function* handle(action: ReturnType<typeof actions.verifyPhone>) {
    try {
      const profile: Profile = yield select(getProfile);
      const phoneId = assert(profile?.primaryPhone?.id, 'no phone in profile on verify request');
      const requestUrl = `${getEnvironmentConfig().services_url}/userprofiles/post/phone/verification`;
      const response: genericResponse<unknown> = yield call(httpClientInterface.send, 'POST', requestUrl, { phoneId });
      log.log('verify phone attempt, response=', response);
      yield* pollForVerification('primaryPhone', actions.verifyPhoneSuccess, actions.verifyPhoneFailure, action.meta);
    } catch (e: any) {
      log.warn('Error with profile phone verification request\n', e);
      yield put(actions.verifyPhoneFailure(e));
      action.meta?.reject?.(e);
    }
  });
}

export function* createPhoneActionWatcher() {
  yield takeLeading(actions.createPhone.type, function* handle(action: ReturnType<typeof actions.createPhone>) {
    try {
      yield delay(12000); // wait 12 seconds before polling
      log.log('createPhone delay processed, beginning polling');
      yield* pollForVerification('primaryPhone', actions.verifyPhoneSuccess, actions.verifyPhoneFailure, action.meta);
    } catch (e: any) {
      log.warn('Error with createPhone saga\n', e);
      yield put(actions.verifyPhoneFailure(e));
      action.meta?.reject?.(e);
    }
  });
}

export function* verifyEmailActionWatcher() {
  yield takeLatest(actions.verifyEmail.type, function* handle(action: ReturnType<typeof actions.verifyEmail>) {
    try {
      const profile: Profile = yield select(getProfile);
      const emailId = assert(profile?.primaryEmail?.id, 'no email in profile on verify request');
      const requestUrl = `${getEnvironmentConfig().services_url}/userprofiles/post/email/verification`;
      const response: genericResponse<unknown> = yield call(httpClientInterface.send, 'POST', requestUrl, { emailId });
      log.log('verify email attempt, response=', response);
      yield* pollForVerification('primaryEmail', actions.verifyEmailSuccess, actions.verifyEmailFailure, action.meta);
    } catch (e: any) {
      log.warn('Error with profile email verification request\n', e);
      yield put(actions.verifyEmailFailure(e));
      action.meta?.reject?.(e);
    }
  });
}

export function* verifyIdentityActionWatcher() {
  yield takeLeading(actions.verifyIdentity.type, function* handle(action: ReturnType<typeof actions.verifyIdentity>) {
    try {
      const requestUrl = `${getEnvironmentConfig().services_url}/sentinel-kyc/api/v2/verify?offering=A2A`;
      const response: genericResponse<verifyIdentityResponse> = yield call(
        httpClientInterface.send,
        'POST',
        requestUrl,
        action.payload,
      );
      log.log('verify identity response=', response);
      if (response.data.status === 'PENDING') {
        // update prefsV2 for pending review display
        yield put(
          preferencesV2Actions.updatePreferenceOverride(
            preferencesV2Types.mkPreferenceV2({
              id: 'identityPending',
              value: true,
            }),
          ),
        );
      }
      yield put(actions.verifyIdentitySuccess(response.data));
      action.meta?.resolve?.(response);
    } catch (e: any) {
      log.warn('Error with profile identity verification request\n', e);
      yield put(actions.verifyIdentityFailure(e));
      action.meta?.reject?.(e);
    }
  });
}

export function* getVerificationStatusActionWatcher() {
  yield takeLeading(actions.getVerificationStatus.type, function* handle() {
    try {
      const requestUrl = `${getEnvironmentConfig().services_url}/sentinel-kyc/api/v2/verification-status?offering=A2A`;
      const response: genericResponse<getVerifyStatusResponse> = yield call(
        httpClientInterface.send,
        'GET',
        requestUrl,
        undefined,
      );
      if (response.data.status === 'PENDING') {
        // update prefsV2 for pending review display
        yield put(
          preferencesV2Actions.updatePreferenceOverride(
            preferencesV2Types.mkPreferenceV2({
              id: 'identityPending',
              value: true,
            }),
          ),
        );
      }
      log.log('verification-status get attempt, response=', response);
      yield put(actions.getVerificationStatusSuccess(response.data));
    } catch (e: any) {
      log.warn('Error in verification-status get request\n', e);
      yield put(actions.getVerificationStatusFailure(e));
    }
  });
}

// for loading watcher sagas
export default [
  getProfileActionWatcher,
  updateProfileActionWatcher,
  createAddressActionWatcher,
  updateAddressActionWatcher,
  verifyPhoneActionWatcher,
  createPhoneActionWatcher,
  verifyEmailActionWatcher,
  verifyIdentityActionWatcher,
  getVerificationStatusActionWatcher,
];
